import type { DriveStep } from "driver.js"

// Stable IDs persist per user, across organizations and devices.
export const tutorials = {
	"onboarding-v1": {
		headerImage: {
			src: "https://assets.orbit.buape.com/orbit_showcase.webp",
			alt: "Orbit workspace showcase",
			width: 9600,
			height: 5400
		},
		title: "Welcome to Orbit",
		description:
			"Try issues, conversations and shared knowledge in a practice workspace.",
		finishTitle: "Thanks for joining us! Here's what to do next.",
		finishDescription:
			"You've explored how work, conversations and knowledge fit together in Orbit. Your practice edits won't be added to your team.",
		subtitle:
			"Orbit is the one-stop-shop for your team's work, discussions, and knowledge.",
		introduction:
			"As an intro to Orbit, join Biteclub, a fictional food-delivery team, for a quick hands-on tour. Improve group ordering, discuss a checkout problem with Kevin and shape the Q4 roadmap.",
		practiceNote:
			"This takes about 3 minutes, or skip ahead whenever you like. Everything you edit stays in the tour and won't be added to your team's workspace.",
		replayNote: "You can replay this tour from User Settings → Tutorials.",
		nextSteps: [
			"Open your team's Issues and start planning your team's work.",
			"Connect a GitHub organization in the Organization settings.",
			"Bring your team along. Invite members from the Organization settings."
		],
		practice: {
			teamName: "Biteclub",
			teamSlug: "bite",
			issueTitle: "Rescue group orders when an item sells out",
			issueDescription: `Friday night, four friends, one takeaway order. Everyone has picked their food, but one dish sells out before the host pays. Today we reject the whole basket with “Something went wrong”. The host has to work out what happened, and everyone risks losing their choices.

## What should happen
- Check item availability again when the host goes to checkout.
- Flag the unavailable dish and show whose item it is.
- Let the host remove it or ask that person to choose something else. Keep everyone else’s food and customisations intact.
- Recalculate the total and delivery fee before asking the host to confirm payment.

## Ready to ship when
- A sold-out item never clears the rest of the basket.
- Nobody is charged until the updated order is confirmed.
- We explain when removing an item takes the basket below the restaurant’s minimum order.

Start with the group-order checkout on iOS and Android. Menu browsing is out of scope for this issue.`,
			comment: `Reproduced this with Noodle House in staging: added two pad thais and a green curry, then marked the curry as sold out in the restaurant dashboard. Checkout fails, but it never tells the host which dish caused it.

I’d keep the basket open and highlight the curry rather than send everyone back to the menu. One edge case: removing it puts this order below the €20 minimum. Can you add how we should handle that before we pick this up? This is part of the November group-order launch in the Q4 roadmap.`,
			articleTitle: "Q4 roadmap · Dinner without the drama",
			articleSummary:
				"Biteclub’s October–December plan: reliable checkout, better group orders and a calmer holiday rush.",
			articleCategory: "Product strategy",
			articleContent: `## Our focus
Biteclub connects hungry people with local restaurants and delivery partners. This quarter, we’re making the awkward parts of ordering feel effortless: a dish selling out, six friends choosing dinner, or a kitchen running behind.

**Quarter owner:** Kevin · Product
**Teams:** Consumer, Restaurant Experience and Delivery Operations
**Status:** Agreed direction; launch dates depend on pilot results.

## October — Get the basics right
**Owner: Consumer team**
- Keep baskets intact after failed payments and let customers retry safely.
- Explain delivery fees and minimum orders before checkout.
- Give support a clear order timeline so customers don’t have to repeat their story.

**Success looks like:** fewer abandoned checkouts and fewer “was I charged twice?” contacts.

## November — Make group orders worth sharing
**Owner: Consumer team, with Restaurant Experience**
- Invite friends with a link; show who is still choosing.
- Give the host a clear cutoff time and a reminder before ordering.
- Rescue the basket when one item sells out, without losing everyone else’s choices.

Pilot with 20 popular restaurants before expanding. Restaurant stock updates must reach checkout reliably before we launch. Split payments stay out of scope this quarter; the host pays for the order.

## December — Take the stress out of busy evenings
**Owner: Delivery Operations**
- Trial scheduled dinner orders with restaurants that can reserve kitchen capacity.
- Update the arrival estimate when preparation runs late, before customers need to ask.
- Let restaurants pause new orders when the kitchen is at capacity.

Roll out gradually ahead of the holiday rush. Keep an immediate rollback available if late deliveries or cancellations increase.

## What we’ll measure
Review checkout completion, group-order conversion, late deliveries and support contacts per 100 orders each week. Compare pilots with similar restaurants outside the rollout; a higher order count alone isn’t enough.

## Open decisions
- How long should a host wait for someone to replace a sold-out dish?
- What should we offer when a scheduled order misses its promised window?

Add rollout risks and proposed decisions here. Track implementation work in issues so this roadmap stays readable.`
		},
		startLabel: "Start the tour",
		dismissLabel: "Not now",
		finishLabel: "Let’s get started",
		welcome: true,
		desktopOnly: false,
		destination: "",
		announcement: undefined,
		steps: [
			{
				id: "status",
				page: "issue",
				target: "properties",
				element: '[data-onboarding-target="properties"]',
				goal: "Set this issue to In Progress using its status control. On smaller screens, properties sit below the issue title.",
				popover: {
					title: "An issue is a piece of work",
					description:
						"Use an issue for a task, bug or idea your team needs to act on. It keeps the plan, owner and conversation together. Status tells everyone where it stands, and changes are recorded in Activity. Changes made to an issue's properties save automatically.",
					side: "left",
					align: "start"
				}
			},
			{
				id: "description",
				page: "issue",
				target: "description",
				element: '[data-onboarding-target="description"]',
				goal: "Add a sentence to the description, then Save changes at the bottom.",
				popover: {
					title: "Give the work some context",
					description:
						"The description explains what needs doing and what a good result looks like. Select text to format it, or type / on a new line to add headings, lists and more.",
					side: "bottom",
					align: "start"
				}
			},
			{
				id: "reply",
				page: "issue",
				target: "comment",
				element: "#comment-practice-comment",
				goal: "Reply to Kevin’s comment with a short update.",
				popover: {
					title: "Keep the conversation with the issue",
					description:
						"Activity records changes and conversations, so teammates can catch up without hunting through dozens of messages on another platform. Replies stay in a thread under the original comment.",
					side: "bottom",
					align: "start"
				}
			},
			{
				id: "reaction",
				page: "issue",
				target: "comment",
				element: "#comment-practice-comment",
				goal: "Open 'Add reaction' on Kevin's comment and choose 'Thumbs up'.",
				popover: {
					title: "A quick reaction is sometimes enough",
					description:
						"Reactions let you acknowledge comments without adding another comment. Your organization's admin can choose new reactions to add to this list in the Settings → Emojis page.",
					side: "bottom",
					align: "start"
				}
			},
			{
				id: "article",
				page: "guide",
				target: "article",
				element: '[data-onboarding-target="article"]',
				goal: "Add a rollout risk or suggestion to the Q4 roadmap, then Save changes.",
				popover: {
					title:
						"A Knowledge Base article is a way to share knowledge or updates with your team",
					description:
						"Issues track a piece of work from start to finish. Knowledge Base articles keep information your team will use again: guides, decisions and how-tos, or updates to keep your team in the loop: roadmaps, RFCs, and patch notes. It uses the same rich editor you've been introduced to in issues and comments.",
					side: "bottom",
					align: "start"
				}
			}
		] satisfies (DriveStep & {
			id: "status" | "description" | "reply" | "reaction" | "article"
			page: "issue" | "guide"
			target: string
			goal: string
		})[]
	},
	"projects-v1": {
		title: "Projects",
		description:
			"Plan Biteclub’s group-order launch with a practice project. Try its dates, members, updates, issues and resources without changing your team's work.",
		welcome: false,
		desktopOnly: false,
		destination: "",
		announcement: undefined,
		practice: {
			name: "Group orders without the drama",
			description:
				"Launch group ordering with 20 popular restaurants before the Friday-night rush. Friends join one basket, the host sees who is still choosing, and a sold-out dish never wipes everyone else's dinner. Kevin owns the pilot. Split payments are out of scope; the host pays.\n\nSuccess: fewer abandoned group baskets and fewer checkout-related support contacts. Review the pilot after two weekends before expanding.",
			issues: [
				"Let friends join a shared basket",
				"Rescue group orders when an item sells out",
				"Warn the host before the group-order cutoff"
			],
			update:
				"The first 20 restaurants are confirmed for the pilot. Shared baskets passed the lunch-rush checks, but sold-out items still need a reliable recovery path before Friday. Sam has handed restaurant onboarding back to Operations, so they no longer need these updates. I’m handing the launch over to you — please confirm the dates and share the next checkpoint. — Kevin",
			resourceTitle: "Q4 roadmap · Dinner without the drama",
			resourceUrl: "https://biteclub.example/roadmap/q4"
		},
		steps: [
			{
				id: "overview",
				target: "project-summary",
				element: '[data-onboarding-target="project-summary"]',
				goal: "Read the Biteclub launch brief, then continue.",
				popover: {
					title: "A project brings related work together",
					description:
						"An issue is one piece of work. A project groups the issues needed to achieve a larger outcome, with a lead, dates, members, updates and shared resources. This is a practice project: your changes stay in this tour.",
					side: "bottom",
					align: "start"
				}
			},
			{
				id: "status",
				target: "project-properties",
				element: '[data-onboarding-target="project-properties"]',
				goal: "Set status to In Progress and select yourself as Lead.",
				popover: {
					title: "Give the launch an owner and a plan",
					description:
						"The project status describes the overall initiative, independently of its issues. Kevin is handing this pilot over to you. Only the assigned lead can post project updates. Status, lead and dates save as soon as you change them.",
					side: "left",
					align: "start"
				}
			},
			{
				id: "dates",
				target: "project-properties",
				element: '[data-onboarding-target="project-properties"]',
				goal: "Choose a different start date and a due date on or after it.",
				popover: {
					title: "Put the pilot on the calendar",
					description:
						"Start date defaults to when the project was created. You can change it to match the actual kickoff, or reset it to the creation date. Due date is your delivery target. Both use the same calendar as issues and save immediately.",
					side: "left",
					align: "start"
				}
			},
			{
				id: "members",
				target: "project-members",
				element: '[data-onboarding-target="project-members"]',
				goal: "Open Members and remove Sam from this project's updates.",
				popover: {
					title: "Keep the right people in the loop",
					description:
						"All active team members join a project automatically, including people who join the team later. You can remove members here and restore them at any time. Removed members stop receiving project-update notifications, but retain their team access. Sam has finished the restaurant handover and no longer needs these updates.",
					side: "top",
					align: "start"
				}
			},
			{
				id: "updates",
				target: "project-updates",
				element: '[data-onboarding-target="project-updates"]',
				goal: "Read Kevin's handover, then post a short update with your next checkpoint.",
				popover: {
					title: "Share progress as the project lead",
					description:
						"Updates give the team a clear view of progress, blockers and next steps. Use Orbit's rich text editor to share yours. Only the current lead can post; if you skipped the handover, go Back and select yourself as Lead. A real update notifies every project member except its author, using their notification preferences. This practice post stays here and sends nothing.",
					side: "bottom",
					align: "start"
				}
			},
			{
				id: "summary",
				target: "project-summary",
				element: '[data-onboarding-target="project-summary"]',
				goal: "Add a sentence to the project description, then Save changes.",
				popover: {
					title: "Keep the outcome clear",
					description:
						"The description gives everyone a place to understand what this project is about. Add a note of your own — perhaps something you would like to try in Biteclub’s group orders. Unlike properties, changes to this text need saving.",
					side: "bottom",
					align: "start"
				}
			},
			{
				id: "issues",
				target: "project-issues",
				element: '[data-onboarding-target="project-issues"]',
				goal: "Use Add existing issue to include the host cutoff warning.",
				popover: {
					title: "Connect the work",
					description:
						"Add existing issues from the team to this project. In your workspace you can also create an issue here. Moving or removing an issue changes its project association; it does not delete the issue. Adding an issue saves immediately.",
					side: "top",
					align: "start"
				}
			},
			{
				id: "progress",
				target: "project-progress",
				element: '[data-onboarding-target="project-progress"]',
				goal: "Review the completed-issue count and progress, then continue.",
				popover: {
					title: "Progress comes from the issues",
					description:
						"Orbit calculates project progress from completed issues. Canceled issues are excluded. Adding unfinished work can lower the percentage. Completing the project itself does not automatically complete its issues.",
					side: "left",
					align: "start"
				}
			},
			{
				id: "resources",
				target: "project-resources",
				element: '[data-onboarding-target="project-resources"]',
				goal: "Add a link titled Pilot checklist with URL https://biteclub.example/pilot, then Save changes.",
				popover: {
					title: "Keep the supporting context close",
					description:
						"Resources link the project to roadmaps, designs and launch checklists. Biteclub's Q4 roadmap is already attached. Add a practice checklist to finish. You can replay this tour using Tutorial on the Projects page.",
					side: "top",
					align: "start"
				}
			}
		] satisfies (DriveStep & { id: string; target: string; goal: string })[]
	},
	"queue-v1": {
		title: "Queue",
		description:
			"Triage a  bug report and accept it into your team's work, with an optional comment.",
		welcome: false,
		desktopOnly: false,
		destination: "",
		announcement: undefined,
		practice: {
			issueTitle:
				"Delivery fee stays stale after changing the checkout address",
			issueDescription: `## Bug report
Changing the delivery address at checkout keeps the fee from the previous address. A customer ordering from Noodle House sees €2.49 for their office, switches to their home outside the inner delivery zone, and still sees €2.49. The payment summary then charges €4.99 without explaining the increase.

## Steps to reproduce
1. Add a ramen bowl from Noodle House to the basket.
2. Go to checkout with the office address selected (€2.49 delivery).
3. Switch to the saved home address (€4.99 delivery).
4. Compare the checkout total with the payment confirmation.

## Expected
Recalculate delivery and the total immediately after an address change. Show the updated price before the customer confirms payment.

## Actual
Checkout retains the old delivery quote. Payment uses the new quote, so the customer pays more than the total they approved.

## Impact
Reported by Support after three customer complaints this morning. Reproduced on iOS 18.6, Biteclub 4.12.0; Android still needs checking.`,
			comment:
				"Triage: reproduced on staging with Noodle House. The address changes, but checkout keeps the cached delivery quote until payment. This isn't a restaurant pricing change. We should pick it up before Friday's dinner rush. Please review and accept it into the team's issues. Add a note for the checkout team if useful."
		},
		steps: [
			{
				id: "report",
				target: "description",
				element: '[data-onboarding-target="description"]',
				goal: "Read the bug report, then continue to Kevin's triage note.",
				popover: {
					title: "Review before committing to the work",
					popoverClass: "orbit-tutorial orbit-tutorial-report",
					description:
						"Queue is where incoming reports wait for a decision. This Biteclub customer saw one delivery price at checkout and paid another. Check the reproduction steps, expected behaviour and impact before accepting work into the team’s main issues view.",
					side: "right" as const,
					align: "start" as const
				}
			},
			{
				id: "triage",
				target: "comment",
				element: "#comment-practice-comment",
				goal: "Read Kevin's findings, then continue to review the issue.",
				popover: {
					title: "Keep the triage context with the report",
					description:
						"Kevin has added some context to help the reviewer understand the issue.",
					side: "bottom" as const,
					align: "start" as const
				}
			},
			{
				id: "accept",
				target: "review",
				element: '[aria-label="Queue review"]',
				goal: "Choose Accept, optionally add a comment, then confirm with Accept issue.",
				popover: {
					title: "Give the checkout team the go-ahead",
					description:
						"Accept moves this report from Queue to To Do. The confirmation includes an optional comment, for example, asking the checkout team to verify Android too. Orbit records your decision and comment in Activity. Decline is for reports that are invalid, like if the reported bug is not reproducible; this reproduced bug is ready to accept.",
					side: "top" as const,
					align: "start" as const
				}
			},
			{
				id: "issues",
				target: "issues",
				element: '[data-tutorial="accepted-issue"]',
				goal: "Find the accepted issue under To Do. Open it to inspect its activity, or finish the tour.",
				popover: {
					title: "From incoming report to planned work",
					description:
						"The bug is now in the main issues view under To Do and no longer awaits triage in Queue. Its report, Kevin's comment and your review stay together. This is a practice issue; finishing the tour removes it without adding anything to your real team.",
					side: "bottom" as const,
					align: "start" as const
				}
			}
		]
	},
	"difficulty-v1": {
		title: "Difficulty",
		description:
			"Choose a difficulty scale that fits your team, with familiar labels on fixed levels from 1 to 5.",
		welcome: false,
		desktopOnly: false,
		destination: "",
		announcement: undefined,
		steps: [
			{
				id: "overview",
				element: '[data-tutorial="difficulty-heading"]',
				popover: {
					title: "Difficulty levels that fit your team",
					description:
						"Difficulty describes how challenging an issue is, rather than how urgently it needs attention (priority serves this purpose). These settings belong to this team, not the whole organization. Different teams can use different words for the same fixed levels.",
					side: "bottom"
				}
			},
			{
				id: "levels",
				element: '[data-tutorial="difficulty-levels"]',
				popover: {
					title: "Five fixed levels",
					description:
						"Level 1 means not difficult; level 5 means very difficult. You can use one level, three, or all five. If you prefer numbered difficulties, you can use levels 2, 4, 8, 16 and 32. The levels are fixed; you can't add more, but you can rename them to suit your team.",
					side: "bottom"
				}
			},
			{
				id: "labels",
				element: '[id^="difficulty-label-"]:not(:disabled)',
				popover: {
					title: "Use words your team understands",
					description:
						"Rename the enabled levels to suit your work\u2014for example, Routine, Involved and Complex. Each label must be unique and no longer than 40 characters. Renaming a level changes its wording, not the difficulty stored on an issue.",
					side: "bottom"
				}
			},
			{
				id: "enabled-levels",
				element: '[id^="difficulty-enabled-"]',
				popover: {
					title: "Offer only the choices you need",
					description:
						"Use the switches to offer between one and five levels. At least one must remain enabled. Turning a level off removes it from new selections; existing issues keep their stored difficulty.",
					side: "bottom"
				}
			},
			{
				id: "none",
				element: '[data-tutorial="difficulty-heading"]',
				popover: {
					title: "None is a separate choice",
					description:
						"Issues can have no difficulty set. None means unassessed, not level 1, and doesn\u2019t count toward your enabled levels. After making changes, use Save changes to apply them to this team. This walkthrough hasn\u2019t changed your settings; use Tutorial here to replay it.",
					side: "bottom"
				}
			}
		] satisfies (DriveStep & { id: string })[]
	},
	"automations-v1": {
		title: "Automations",
		description:
			"Let Orbit handle repeatable work. Take a quick tour of triggers, conditions and actions, then learn how to control when a rule runs.",
		welcome: false,
		desktopOnly: false,
		destination: "",
		announcement: undefined,
		steps: [
			{
				id: "trigger",
				element: '[data-tutorial="automation-trigger"]',
				popover: {
					title: "Start with an event",
					description:
						"An automation is a rule that handles routine work for your team. Its trigger decides when Orbit checks the rule\u2014for example, when an issue is created or its status changes. This walkthrough only explains the editor; it won\u2019t save or enable a rule.",
					side: "bottom"
				}
			},
			{
				id: "conditions",
				element: '[data-tutorial="automation-condition"]',
				popover: {
					title: "Only act when it makes sense",
					description:
						"Conditions narrow down which issues qualify. For example, react to an issue being created only when its priority is Urgent. Add a condition to combine checks with AND or OR. Without a condition, the actions apply whenever the trigger matches.",
					side: "bottom"
				}
			},
			{
				id: "actions",
				element: '[data-tutorial="automation-actions"]',
				popover: {
					title: "Choose what happens next",
					description:
						"Actions are the work Orbit performs after the trigger and conditions match. Add the actions you need and configure each one. A condition can also have an Otherwise branch for issues that don\u2019t match. Available actions depend on your team\u2019s enabled integrations.",
					side: "bottom"
				}
			},
			{
				id: "name",
				element: '[data-tutorial="automation-name"]',
				popover: {
					title: "Give the rule a clear purpose",
					description:
						"Name the rule so teammates can understand it later\u2014for example, \u201cFlag urgent delivery incidents\u201d. Use the description to explain why it exists and what it changes.",
					side: "bottom"
				}
			},
			{
				id: "frequency",
				element: '[data-tutorial="automation-frequency"]',
				popover: {
					title: "Control repeated runs",
					description:
						"Every match allows the rule to run whenever it qualifies. Once per issue prevents it from running again for the same issue. Choose the mode that fits the work, especially for actions that post updates or notify people.",
					side: "bottom"
				}
			},
			{
				id: "owner",
				element: '[data-tutorial="automation-owner"]',
				popover: {
					title: "Choose who the rule runs as",
					description:
						"The owner provides the permissions used to execute the automation. Choose a teammate with the access the actions need; an automation doesn\u2019t bypass permissions.",
					side: "bottom"
				}
			},
			{
				id: "enable",
				element: '[data-tutorial="automation-enabled"]',
				popover: {
					title: "Review, save and follow up",
					description:
						"Enabled controls whether the rule can run. Finish configuring the rule, then use Save changes when you\u2019re ready. After saving, Open runs takes you to preview and execution history so you can check what happened. You can replay this walkthrough using Tutorial in this editor\u2019s header.",
					side: "bottom"
				}
			}
		] satisfies (DriveStep & { id: string })[]
	},
	"tutorial-demo": {
		title: "Workspace tabs",
		description:
			"Learn how to open another Orbit tab and keep your tasks in view.",
		welcome: false,
		desktopOnly: true,
		destination: "",
		announcement: {
			id: "workspace-tabs-demo-v1",
			releasedAt: "2026-10-19T00:00:00Z",
			title: "A home for every task",
			description:
				"Keep issues and documents open without losing your place. Want a quick look at workspace tabs?"
		},
		steps: [
			{
				id: "new-tab",
				element: '[data-tutorial="workspace-new-tab"]',
				popover: {
					title: "Make room for another task",
					description:
						"Use the + button to open another Orbit workspace tab without replacing your current page.",
					side: "bottom"
				}
			},
			{
				id: "switch-tabs",
				element: '[data-tutorial="workspace-tabs"]',
				popover: {
					title: "Keep your place",
					description:
						"Switch between open issues, documents and pages here. Each tab keeps its own draft and scroll position. Closing a tab with unsaved changes asks for confirmation.",
					side: "bottom"
				}
			},
			{
				id: "manage-tabs",
				element: '[data-tutorial="workspace-tabs"]',
				popover: {
					title: "Pin tabs or close them in bulk",
					description:
						"Right-click an Orbit tab and choose Pin tab to keep it at the left of your tab list. Use Close all tabs in the same menu to clear unpinned tabs; pinned tabs stay open.",
					side: "bottom"
				}
			},
			{
				id: "open-links",
				element: '[data-tutorial="workspace-new-tab"]',
				popover: {
					title: "Open links in Orbit tabs, too",
					description:
						"Middle-click an internal link, or right-click it and choose Open in Orbit tab. Continue finishes this tour; you can replay it anytime from Tutorials settings.",
					side: "bottom"
				}
			}
		] satisfies (DriveStep & { id: string })[]
	}
}

export const tutorialIds = Object.keys(tutorials) as (keyof typeof tutorials)[]

export function tutorialAnnouncementKey(id: keyof typeof tutorials) {
	const tutorial = tutorials[id]
	if (!("announcement" in tutorial) || !tutorial.announcement) return undefined
	return `announcement:${id}:${tutorial.announcement.id}`
}

// Pick exactly one login experience. Account age is irrelevant for releases.
// Welcome wins; acknowledging an invitation never completes the actual tour.
export function getLoginTutorial(
	preferences: {
		globalHideTutorials: boolean
		tutorialHistory: Record<string, "completed" | "dismissed">
	},
	now = Date.now()
) {
	if (preferences.globalHideTutorials) return undefined
	const welcome = tutorialIds.find((id) => {
		const tutorial = tutorials[id]
		return (
			"welcome" in tutorial &&
			tutorial.welcome &&
			!preferences.tutorialHistory[id]
		)
	})
	if (welcome) return { id: welcome, kind: "welcome" as const }
	const announcement = tutorialIds
		.filter((id) => {
			const tutorial = tutorials[id]
			const key = tutorialAnnouncementKey(id)
			return (
				"announcement" in tutorial &&
				tutorial.announcement &&
				key &&
				Date.parse(tutorial.announcement.releasedAt) <= now &&
				!preferences.tutorialHistory[key]
			)
		})
		.sort(
			(a, b) =>
				Date.parse(tutorials[b].announcement?.releasedAt ?? "") -
				Date.parse(tutorials[a].announcement?.releasedAt ?? "")
		)[0]
	return announcement
		? { id: announcement, kind: "announcement" as const }
		: undefined
}
